import { MemberUtils } from "@shared/member.utils";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import * as SentryUtils from "../utils/sentry";
import { BackButton } from "../components/common/BackButton";
import { Button } from "../components/common/Button";
import {
  PageTitle,
  PageTitleDetail,
  PageTitleSubtitle,
} from "../components/common/PageData";
import { useRegistrationFlowStore } from "../stores/registrationFlow.store";
import { useSeasonStore } from "../stores/season.store";
import { useProfileOwner, useUserStore } from "../stores/user.store";
import { getMemberIcon, getMemberTypeLabel } from "../styles/member";

const PageContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 8px;
`;

const Callout = styled.h2`
  font-size: 18px;
  margin-bottom: 8px;
`;

const MemberCard = styled.div`
  border: 1px solid #000000;
  border-radius: 8px;
  margin-bottom: 16px;
`;

const MemberContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
`;

const MemberInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const MemberIcon = styled.div`
  width: 40px;
  height: 40px;
  background-color: #f5f5f5;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const MemberName = styled.h3`
  font-size: 18px;
  margin: 0;
`;

const MemberTypeP = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
`;

const EditButton = styled.button`
  background-color: #000;
  color: white;
  padding: 8px 16px;
  border-radius: 0 0 4px 4px;
  font-size: 16px;
  font-weight: 600;
  width: 100%;
`;

const FooterInfo = styled.p`
  font-size: 14px;
  color: #666;
  margin: 24px 0;
  text-align: center;
`;

export const FamilyMembers = () => {
  const navigate = useNavigate();
  const { profile } = useUserStore();
  const profileOwner = useProfileOwner();
  const { currentSeason, latestSeason } = useSeasonStore();
  const { firstRegistration } = useRegistrationFlowStore();
  const referenceSeason = currentSeason ?? latestSeason;

  const handleEditMember = (memberId: string) => {
    navigate(`/account/members/${memberId}`);
  };

  if (profile === null) {
    SentryUtils.captureMessage(
      "FamilyMembers: profile is null - inconsistent state",
      {
        tags: {
          component: "FamilyMembers",
          issue: "inconsistent_member_state",
        },
        level: "warning",
      },
    );
    return null;
  }

  return (
    <PageContainer>
      <BackButton onClick={() => navigate("/account")} />
      <Header>
        <div>
          <PageTitle>GESTIONE COMPONENTI FAMIGLIA</PageTitle>
          <PageTitleSubtitle>
            {profileOwner?.first_name} {profileOwner?.last_name}
          </PageTitleSubtitle>
          {profile.created_at && (
            <PageTitleDetail>
              ISCRITTO IL {new Date(profile.created_at).toLocaleDateString()}
            </PageTitleDetail>
          )}
        </div>
      </Header>

      <Callout>GESTISCI TUTTI I COMPONENTI DELLA TUA FAMIGLIA</Callout>

      {profile.members
        .sort(
          (a, b) =>
            new Date(a.created_at ?? 0).getTime() -
            new Date(b.created_at ?? 0).getTime(),
        )
        .map((member) => {
          const memberType = referenceSeason
            ? MemberUtils.getMemberType(member.date_of_birth, referenceSeason)
            : null;
          return (
            <MemberCard key={member.id}>
              <MemberContent>
                <MemberInfo>
                  <div>
                    <MemberName>
                      {member.first_name} {member.last_name}
                    </MemberName>
                    {memberType && (
                      <MemberTypeP>{getMemberTypeLabel(memberType)}</MemberTypeP>
                    )}
                  </div>
                </MemberInfo>
                {memberType && (
                  <MemberIcon>
                    {/* TODO: one icon for each type */}
                    <img src={getMemberIcon(memberType)}></img>
                  </MemberIcon>
                )}
              </MemberContent>
              <EditButton onClick={() => handleEditMember(member.id)}>
                MODIFICA DATI
              </EditButton>
            </MemberCard>
          );
        })}

      <FooterInfo>
        SE DESIDERI PUOI AGGIUNGERE ALTRI COMPONENTI DELLA TUA FAMIGLIA,
        OVVIAMENTE NON CANI E GATTI, GRAZIE.
      </FooterInfo>

      <Button fullWidth onClick={() => navigate("/account/members/new")}>
        AGGIUNGI COMPONENTE
      </Button>
      <div style={{ height: "20px" }} />
      {firstRegistration && currentSeason && (
        <Button fullWidth onClick={() => navigate("/checkout")}>
          PROCEDI AL PAGAMENTO
        </Button>
      )}
    </PageContainer>
  );
};
